import { getAdvertiserNameById } from "advertiser/tools/common"
import { LOCALES } from "app/language/model"
import { translate } from "app/language/service"
import { ICsvFileColumn } from "app/model"
import { getStore } from "app/store/store"
import { downloadCsvFile, generateCsvFileContent } from "app/tools/file"
import { formatDate } from "app/util/date/tools/common"
import { ALL_FORECAST_STATUSES, ICampaignExportRow, IDspDealExportRow, IForecastExportRow } from "campaign/model"
import { campaignRPO } from "campaign/repository"
import { CURRENCIES } from "currency/model"
import { IDspDeal } from "dsp-deal/model"
import { getDspDealsByCampaignId } from "dsp-deal/tools/common"
import { getDspNameById } from "dsp/tools/common"
import { getCurrencySymbol, getEcpm } from "form/inventory/tools/common"
import { messageSVC } from "message/service"
import { getOrderNameById } from "order/tools/common"
import { IReportsCampaignResult } from "reports/model"
import { getCampaignsReports } from "reports/tools/campaign"
import { campaignStatusHaveReport } from "./reports"
import { getStatusTranslation } from "./status"

export async function generateAndDownloadCampaignsCsvFile(isForecast: boolean, isDspDeal: boolean): Promise<void> {
  try {
    const columns: ICsvFileColumn[] = isForecast
      ? getForecastsColumns()
      : isDspDeal
        ? getDspDealColumns()
        : getCampaignsColumns()
    const fileContent: string = generateCsvFileContent(
      columns,
      await (isForecast ? getForecastExportRows() : isDspDeal ? getDspDealExportRows() : getCampaignExportRows())
    )
    const fileName: string = `${isForecast ? "forecast" : isDspDeal ? "dsp-deal" : "campaign"}-list-${formatDate(new Date())}`
    downloadCsvFile(fileName, fileContent)
  } catch (error) {
    console.error(error)
    messageSVC.error(translate("campaign.export.error"))
  }
}

const getDspDealColumns = (): ICsvFileColumn[] => [
  {
    key: "id",
    label: "Id"
  },
  {
    key: "name",
    label: translate("label.name")
  },
  {
    key: "dealid",
    label: "Dealid"
  },
  {
    key: "status",
    label: translate("label.status")
  },
  {
    key: "language",
    label: translate("label.language")
  },
  {
    key: "created_at",
    label: translate("label.creationDate")
  },
  {
    key: "start_date",
    label: translate("label.startDate")
  },
  {
    key: "end_date",
    label: translate("label.endDate")
  },
  {
    key: "orderId",
    label: `${translate("reports.column.order_id")} ID`
  },
  {
    key: "orderName",
    label: translate("reports.column.order_id")
  },
  {
    key: "dspId",
    label: "DSP ID"
  },
  {
    key: "dspName",
    label: translate("label.dsp.name")
  },
  {
    key: "bidFloor",
    label: translate("label.bid-floor")
  },
  {
    key: "auctionType",
    label: translate("label.auction")
  }
]

const getCampaignsColumns = (): ICsvFileColumn[] => [
  {
    key: "id",
    label: "Id"
  },
  {
    key: "name",
    label: translate("label.name")
  },
  {
    key: "status",
    label: translate("label.status")
  },
  {
    key: "language",
    label: translate("label.language")
  },
  {
    key: "created_at",
    label: translate("label.creationDate")
  },
  {
    key: "start_date",
    label: translate("label.startDate")
  },
  {
    key: "end_date",
    label: translate("label.endDate")
  },
  {
    key: "orderId",
    label: `${translate("reports.column.order_id")} ID`
  },
  {
    key: "orderName",
    label: translate("reports.column.order_id")
  },
  {
    key: "advertiserId",
    label: `${translate("label.advertiser")} ID`
  },
  {
    key: "budget",
    label: translate("label.budget")
  },
  {
    key: "advertiserName",
    label: translate("label.advertiser")
  },
  {
    key: "bids",
    label: translate("reports.column.bids")
  },
  {
    key: "buys",
    label: translate("reports.column.buys")
  },
  {
    key: "delivery_fees",
    label: translate("reports.column.delivery_fees")
  },
  {
    key: "screen_count",
    label: translate("reports.column.screen_count")
  },
  {
    key: "ecpm",
    label: translate("reports.column.ecpm")
  },
  {
    key: "impressions",
    label: translate("reports.column.impressions")
  },
  {
    key: "media_count",
    label: translate("reports.column.media_count")
  },
  {
    key: "spend",
    label: translate("reports.column.spend")
  },
  {
    key: "ssp_spend",
    label: translate("reports.column.ssp_spend")
  },
  {
    key: "wins",
    label: translate("reports.column.wins")
  },
  {
    key: "estimate_ecpm",
    label: translate("label.estimated.total_ecpm")
  },
  {
    key: "estimate_total_screens",
    label: translate("label.estimated.total_screens")
  },
  {
    key: "estimate_total_impressions",
    label: translate("label.estimated.total_impressions")
  },
  {
    key: "estimate_total_spend",
    label: translate("label.estimated.total_spend")
  }
]

export const getSspDealColumns = (): ICsvFileColumn[] => [
  {
    key: "id",
    label: "Id"
  },
  {
    key: "name",
    label: translate("label.name")
  },
  {
    key: "status",
    label: translate("label.status")
  },
  {
    key: "language",
    label: translate("label.language")
  },
  {
    key: "created_at",
    label: translate("label.creationDate")
  },
  {
    key: "start_date",
    label: translate("label.startDate")
  },
  {
    key: "end_date",
    label: translate("label.endDate")
  },
  {
    key: "orderId",
    label: `${translate("reports.column.order_id")} ID`
  },
  {
    key: "orderName",
    label: translate("reports.column.order_id")
  },
  {
    key: "bids",
    label: translate("reports.column.bids")
  },
  {
    key: "buys",
    label: translate("reports.column.buys")
  },
  {
    key: "delivery_fees",
    label: translate("reports.column.delivery_fees")
  },
  {
    key: "screen_count",
    label: translate("reports.column.screen_count")
  },
  {
    key: "ecpm",
    label: translate("reports.column.ecpm")
  },
  {
    key: "impressions",
    label: translate("reports.column.impressions")
  },
  {
    key: "media_count",
    label: translate("reports.column.media_count")
  },
  {
    key: "spend",
    label: translate("reports.column.spend")
  },
  {
    key: "ssp_spend",
    label: translate("reports.column.ssp_spend")
  },
  {
    key: "wins",
    label: translate("reports.column.wins")
  },
  {
    key: "estimate_ecpm",
    label: translate("label.estimated.total_ecpm")
  },
  {
    key: "estimate_total_screens",
    label: translate("label.estimated.total_screens")
  },
  {
    key: "estimate_total_impressions",
    label: translate("label.estimated.total_impressions")
  },
  {
    key: "estimate_total_spend",
    label: translate("label.estimated.total_spend")
  }
]

const getForecastsColumns = (): ICsvFileColumn[] => [
  {
    key: "id",
    label: "Id"
  },
  {
    key: "name",
    label: translate("label.name")
  },
  {
    key: "budget",
    label: translate("label.budget")
  },
  {
    key: "estimate_ecpm",
    label: translate("label.estimated.total_ecpm")
  },
  {
    key: "estimate_total_screens",
    label: translate("label.estimated.total_screens")
  },
  {
    key: "estimate_total_impressions",
    label: translate("label.estimated.total_impressions")
  },
  {
    key: "start_date",
    label: translate("label.startDate")
  },
  {
    key: "end_date",
    label: translate("label.endDate")
  },
  {
    key: "created_at",
    label: translate("label.creationDate")
  },
  {
    key: "language",
    label: translate("label.language")
  }
]

const getForecastExportRows = async (): Promise<IForecastExportRow[]> => {
  const { pagination, filters } = getStore().getState().campaign.forecastList
  const forecasts = await campaignRPO.getCampaigns(
    {
      ...pagination,
      page: 0,
      pageSize: 10000
    },
    {
      ...filters,
      statuses: ALL_FORECAST_STATUSES.slice()
    }
  )
  const allForecastIds: number[] = []
  const rows: IForecastExportRow[] = forecasts.map(c => {
    allForecastIds.push(c.id)
    return {
      id: c.id,
      currency_id: c.currency_id as CURRENCIES,
      name: c.product || "",
      language: (c.language_id as LOCALES) || "",
      created_at: c.created_at ? c.created_at.toISOString() : "",
      start_date: c.start_date ? c.start_date.toISOString() : "",
      end_date: c.end_date ? c.end_date.toISOString() : "",
      budget: `${c.budget} ${getCurrencySymbol(c.currency_id)}`,
      estimate_ecpm: null, // display currency
      estimate_total_screens: null,
      estimate_total_impressions: null,
      estimate_total_spend: null // display currency
    }
  })
  const estimates = await campaignRPO.getCampaignsGlobalForecast(allForecastIds)
  for (const row of rows) {
    const estimate = estimates[row.id]
    if (estimate) {
      row.estimate_total_impressions = estimate.total_impressions
      row.estimate_total_screens = estimate.total_screens
      row.estimate_total_spend = `${estimate.total_spend} ${getCurrencySymbol(row.currency_id)}`
      row.estimate_ecpm = `${getEcpm(estimate.total_spend, estimate.total_impressions)} ${getCurrencySymbol(
        row.currency_id
      )}`
    }
  }
  return rows
}

const getDspDealExportRows = async (): Promise<IDspDealExportRow[]> => {
  const { pagination, filters } = getStore().getState().campaign.dspDealList
  const campaigns = await campaignRPO.getCampaigns(
    {
      ...pagination,
      page: 0,
      pageSize: 10000
    },
    filters,
    "Deal"
  )
  const dspDeals = await getDspDealsByCampaignId(campaigns.map(c => c.id))
  const dspDealByCampaignId: { [campaignId: number]: IDspDeal } = {}
  for (const dspDeal of dspDeals) {
    dspDealByCampaignId[dspDeal.campaign_id] = dspDeal
  }
  const allCampaignIds: number[] = []
  const rows: IDspDealExportRow[] = campaigns.map(c => {
    allCampaignIds.push(c.id)
    const dspDeal = dspDealByCampaignId[c.id]
    return {
      id: c.id,
      currency_id: c.currency_id as CURRENCIES,
      name: c.product || "",
      language: (c.language_id as LOCALES) || "",
      created_at: c.created_at ? c.created_at.toISOString() : "",
      start_date: c.start_date ? c.start_date.toISOString() : "",
      end_date: c.end_date ? c.end_date.toISOString() : "",
      status: getStatusTranslation(c.status_id),
      orderId: c.order_id || -1,
      orderName: typeof c.order_id === "number" ? getOrderNameById(c.order_id) : "",
      dealid: dspDeal ? dspDeal.dealid : "-",
      dspId: dspDeal ? dspDeal.dsp_id : -1,
      dspName: dspDeal ? getDspNameById(dspDeal.dsp_id) : "-",
      bidFloor: dspDeal ? (dspDeal.bid_floor as number) : -1,
      auctionType: dspDeal ? translate(`label.auction.${dspDeal.auction_type}`) : "-"
    }
  })
  return rows
}

const getCampaignExportRows = async (): Promise<ICampaignExportRow[]> => {
  const { pagination, filters } = getStore().getState().campaign.campaignList
  const campaigns = await campaignRPO.getCampaigns(
    {
      ...pagination,
      page: 0,
      pageSize: 10000
    },
    filters
  )
  const allCampaignIds: number[] = []
  const campaignsIdsHavingReports: number[] = []
  const rows: ICampaignExportRow[] = campaigns.map(c => {
    allCampaignIds.push(c.id)
    if (campaignStatusHaveReport(c.status_id)) {
      campaignsIdsHavingReports.push(c.id)
    }
    return {
      id: c.id,
      currency_id: c.currency_id as CURRENCIES,
      name: c.product || "",
      language: (c.language_id as LOCALES) || "",
      created_at: c.created_at ? c.created_at.toISOString() : "",
      start_date: c.start_date ? c.start_date.toISOString() : "",
      end_date: c.end_date ? c.end_date.toISOString() : "",
      status: getStatusTranslation(c.status_id),
      orderId: c.order_id || -1,
      orderName: typeof c.order_id === "number" ? getOrderNameById(c.order_id) : "",
      advertiserId: c.advertiser_id || -1,
      advertiserName: typeof c.advertiser_id === "number" ? getAdvertiserNameById(c.advertiser_id) : "",
      budget: `${c.budget} ${getCurrencySymbol(c.currency_id)}`,
      // report
      bids: null,
      buys: null,
      delivery_fees: null,
      screen_count: null,
      ecpm: null, // display currency
      impressions: null,
      media_count: null,
      spend: null, // display currency
      ssp_spend: null, // display currency
      wins: null,
      // estimates data
      estimate_ecpm: null, // display currency
      estimate_total_screens: null,
      estimate_total_impressions: null,
      estimate_total_spend: null // display currency
    }
  })
  const reports: { [campaignId: number]: IReportsCampaignResult } = {}
  if (campaignsIdsHavingReports.length !== 0) {
    const reportsRows = await getCampaignsReports(campaignsIdsHavingReports)
    for (const reportsRow of reportsRows) {
      reports[reportsRow.campaign_id] = reportsRow
    }
  }
  const estimates = await campaignRPO.getCampaignsGlobalForecast(allCampaignIds)
  for (const row of rows) {
    const report = reports[row.id]
    if (report) {
      row.bids = report.bids
      row.buys = report.buys
      row.delivery_fees = report.delivery_fees
      row.screen_count = report.screen_count
      row.ecpm = `${report.ecpm} ${getCurrencySymbol(row.currency_id)}`
      row.impressions = report.impressions
      row.media_count = report.media_count
      row.spend = `${report.spend} ${getCurrencySymbol(row.currency_id)}`
      row.ssp_spend = `${report.ssp_spend} ${getCurrencySymbol(row.currency_id)}`
      row.wins = report.wins
    }
    const estimate = estimates[row.id]
    if (estimate) {
      row.estimate_total_impressions = estimate.total_impressions
      row.estimate_total_screens = estimate.total_screens
      row.estimate_total_spend = `${estimate.total_spend} ${getCurrencySymbol(row.currency_id)}`
      row.estimate_ecpm = `${getEcpm(estimate.total_spend, estimate.total_impressions)} ${getCurrencySymbol(
        row.currency_id
      )}`
    }
  }
  return rows
}
